/**
 * Authentication state.
 *
 * The access token is the source of truth for "is someone signed in"; the
 * profile from `/api/auth/me` is the source of truth for "what may they do".
 * That split is unchanged from the Supabase version — only what issues the
 * token moved.
 *
 * The Base44 version reported four states — loading settings, loading auth,
 * an error object, and authenticated — and mishandled two of them. With no
 * token at all it set no error and rendered the whole application shell to an
 * anonymous visitor, who then saw a "Connection Error" toast on every page. An
 * unrecognised error type fell through the same way.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { hasPermission } from '@stockwise/shared/permissions';

import { api, ApiError } from './api.js';
import * as auth from './auth-client.js';

const AuthContext = createContext(null);

/** Every state the shell can be in. Exactly one is true at a time. */
export const AUTH_STATE = {
  LOADING: 'loading',
  SIGNED_OUT: 'signed_out',
  /** Signed in, but the profile is inactive or missing. */
  NOT_PROVISIONED: 'not_provisioned',
  READY: 'ready',
  /** Signed in, but the API could not be reached to load the profile. */
  UNAVAILABLE: 'unavailable',
};

/**
 * Tells the app's other tabs when someone signs in or out here.
 *
 * Every tab shares one refresh cookie, so a sign-in on a shared iPad would
 * otherwise leave an older tab showing the previous person's name and
 * permissions while acting as the new one.
 */
const authChannel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('stockwise-auth') : null;

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState(null);
  // There is no configuration to have failed at import time any more, so this
  // always starts as LOADING and `bootstrap()` decides within one round trip.
  const [state, setState] = useState(AUTH_STATE.LOADING);
  const [problem, setProblem] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const me = await api.get('/api/auth/me');
      setProfile(me);
      setProblem(null);
      setState(AUTH_STATE.READY);
    } catch (error) {
      setProfile(null);
      if (error instanceof ApiError && error.status === 403) {
        // Signed in, but deactivated or deleted. The message from the server
        // says which, and is worth showing verbatim.
        setProblem(error.message);
        setState(AUTH_STATE.NOT_PROVISIONED);
      } else if (error instanceof ApiError && error.status === 401) {
        setState(AUTH_STATE.SIGNED_OUT);
      } else {
        setProblem(error.message);
        setState(AUTH_STATE.UNAVAILABLE);
      }
    }
  }, []);

  useEffect(() => {
    let active = true;

    // One request on load: is the refresh cookie still good? This replaces
    // Supabase reading a session out of localStorage, and is the reason a
    // reload does not flash the sign-in screen.
    auth.bootstrap().then(
      (signedIn) => {
        if (!active) return;
        if (signedIn) loadProfile();
        else setState(AUTH_STATE.SIGNED_OUT);
      },
      () => {
        // The server could not be asked. Not the same as signed out.
        if (!active) return;
        setProblem(null);
        setState(AUTH_STATE.UNAVAILABLE);
      },
    );

    // Fires only when the token appears or disappears, never on the silent
    // rotations in between — refetching the profile every fifteen minutes
    // would be pointless traffic. (The Supabase version skipped
    // TOKEN_REFRESHED for exactly the same reason.)
    const unsubscribe = auth.onAuthChange((token) => {
      if (!active) return;
      if (!token) {
        setProfile(null);
        // Whoever signs in next must not see this person's cached pages.
        queryClient.clear();
        setState(AUTH_STATE.SIGNED_OUT);
      }
    });

    const onOtherTab = (event) => {
      if (event.data === 'signed-out') auth.clearSession();
      else if (event.data === 'signed-in') window.location.reload();
    };
    authChannel?.addEventListener('message', onOtherTab);

    return () => {
      active = false;
      unsubscribe();
      authChannel?.removeEventListener('message', onOtherTab);
    };
  }, [loadProfile, queryClient]);

  const signIn = useCallback(
    async (email, password) => {
      await auth.signIn(email, password);
      authChannel?.postMessage('signed-in');
      setState(AUTH_STATE.LOADING);
      await loadProfile();
    },
    [loadProfile],
  );

  const signOut = useCallback(async () => {
    await auth.signOut();
    authChannel?.postMessage('signed-out');
    setProfile(null);
    queryClient.clear();
    setState(AUTH_STATE.SIGNED_OUT);
  }, [queryClient]);

  const sendPasswordReset = useCallback(async (email) => {
    // Always resolves, whether or not the address exists — the server refuses
    // to say, so that nobody can use this to find out who has an account.
    await auth.requestPasswordReset(email);
  }, []);

  const value = useMemo(
    () => ({
      state,
      problem,
      user: profile,
      isReady: state === AUTH_STATE.READY,
      can: (flag) => hasPermission(profile, flag),
      signIn,
      signOut,
      sendPasswordReset,
      refreshProfile: loadProfile,
    }),
    [state, problem, profile, signIn, signOut, sendPasswordReset, loadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/** Convenience for the common `can(...)` check. */
export function usePermission(flag) {
  const { can } = useAuth();
  return can(flag);
}
